import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/** App-wide, non-blocking toasts (rendered by <Toaster /> in App.tsx). Several can show at once.
 *  Text is a backend `messageCode` (i18n `messages.<code>`, with optional `params`). Dispatch `pushToast(...)` from anywhere — SAM's SHOW_FEEDBACK uses this too. */
export type ToastLevel = 'INFO' | 'SUCCESS' | 'WARNING';

export interface Toast {
  id: string;
  level: ToastLevel;
  messageCode: number;
  params?: Record<string, string | number>;
  durationMs: number;
}

export type ToastInput = Omit<Toast, 'id' | 'durationMs' | 'level'> & Partial<Pick<Toast, 'durationMs' | 'level'>>;

const MAX_VISIBLE = 4;
let seq = 0;

const toastSlice = createSlice({
  name: 'toast',
  initialState: { items: [] as Toast[] },
  reducers: {
    pushToast: {
      reducer(state, action: PayloadAction<Toast>) {
        state.items.push(action.payload);
        if (state.items.length > MAX_VISIBLE) state.items.splice(0, state.items.length - MAX_VISIBLE);
      },
      prepare(input: ToastInput) {
        seq += 1;
        return { payload: { level: 'INFO' as ToastLevel, durationMs: 4000, ...input, id: `t${Date.now()}-${seq}` } };
      },
    },
    dismissToast(state, action: PayloadAction<string>) {
      state.items = state.items.filter((t) => t.id !== action.payload);
    },
  },
});

export const { pushToast, dismissToast } = toastSlice.actions;
export default toastSlice.reducer;
